import { compareBoardColumns, getColumnDeleteTargetColumns } from '@aline/common';
import { Apollo } from 'src/gql/generated';

import { getNonSigningStartedColumns } from '../../phase-column-helpers';

export type Column = Pick<Apollo.Column, 'category' | 'id' | 'name' | 'sortKey' | 'color'>;

/**
 * Triage and Signing are board-managed singletons whose name, category, and color are fixed, so
 * their rows offer no edit or recolor affordance. Both can still be deleted: that is how a board
 * turns triage off.
 */
export function getIsColumnLocked(column: Column, signingColumnId: string | null): boolean {
  return column.category === Apollo.ColumnCategory.Triage || column.id === signingColumnId;
}

/**
 * Where workflows go when Signing is deleted. Asking the user is pointless: Signing only
 * ever holds signing work, the server refuses the delete while any signing phase exists,
 * and anything left behind belongs with the board's ordinary Started work. Returns null
 * when the board has no other Started column to absorb them.
 */
export function getSigningColumnDeleteTargetId(
  columns: Column[],
  signingColumnId: string
): string | null {
  return getNonSigningStartedColumns(columns, signingColumnId).at(0)?.id ?? null;
}

/**
 * Where workflows can move when a column is deleted, in board order. Shares its rule with the
 * server, so every column offered here is one the delete will accept.
 */
export function getColumnDeleteTargets(args: {
  allColumns: Column[];
  column: Column;
  signingColumnId?: string | null;
}): Column[] {
  const { allColumns, column, signingColumnId } = args;

  return getColumnDeleteTargetColumns({
    columns: [...allColumns].sort(compareBoardColumns),
    deletedColumn: column,
    signingColumnId,
  });
}

export function getColumnDeletionBlockedMessage(args: {
  allColumns: Column[];
  column: Column;
  signingColumnId: string | null;
}): string | null {
  const { allColumns, column, signingColumnId } = args;
  if (column.id === signingColumnId) return null;

  const categoryColumnCount = allColumns.filter(
    boardColumn => boardColumn.category === column.category
  ).length;

  switch (column.category) {
    case Apollo.ColumnCategory.Started: {
      const nonSigningStartedCount = getNonSigningStartedColumns(
        allColumns,
        signingColumnId
      ).length;
      return nonSigningStartedCount <= 1
        ? 'You cannot delete the last non-signing Started column.'
        : null;
    }
    case Apollo.ColumnCategory.Done:
      return categoryColumnCount <= 1 ? 'You cannot delete the last Done column.' : null;
    case Apollo.ColumnCategory.Paused:
      return categoryColumnCount <= 1 ? 'You cannot delete the last Paused column.' : null;
    case Apollo.ColumnCategory.Cancelled:
      return categoryColumnCount <= 1 ? 'You cannot delete the last Cancelled column.' : null;
    // Neither category is a board floor: a board runs fine with no Triage and no Not Started.
    case Apollo.ColumnCategory.NotStarted:
    case Apollo.ColumnCategory.Triage:
      return null;
  }
}

export function getCategoryLabel(category: Apollo.ColumnCategory): string {
  switch (category) {
    case Apollo.ColumnCategory.Triage:
      return 'Triage';
    case Apollo.ColumnCategory.NotStarted:
      return 'Not Started';
    case Apollo.ColumnCategory.Started:
      return 'Started';
    case Apollo.ColumnCategory.Done:
      return 'Done';
    case Apollo.ColumnCategory.Paused:
      return 'Paused';
    case Apollo.ColumnCategory.Cancelled:
      return 'Cancelled';
    default:
      return category;
  }
}
